import { useState, useRef, useEffect } from "react";
import { Send, Cookie, User, Bot, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CHAT_API = "/api/chat";

// Simple markdown-like rendering: bold, newlines, list items
function formatMessage(text) {
  if (!text) return null;
  
  return text.split("\n").map((line, i) => {
    // Bold **text** or __text__
    const parts = line.split(/(\*\*.*?\*\*|__.*?__)/g).map((part, j) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("__") && part.endsWith("__")) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });

    // Detect list items
    const trimmed = line.trim();
    if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      return (
        <div key={i} className="flex gap-2 ml-2">
          <span className="text-muted-foreground">•</span>
          <span>{parts}</span>
        </div>
      );
    }

    // Empty line = spacing
    if (trimmed === "") {
      return <div key={i} className="h-2" />;
    }

    return (
      <div key={i}>
        {parts}
      </div>
    );
  });
}

export default function ChatBot({ userName = "Utilisateur" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      sender: "user",
      text: trimmed,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const finalPrompt = `${userName} : ${trimmed}`;
      const userParam = removeAccents(userName);

      const token = localStorage.getItem("home-token");
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120_000); // 120s

      const response = await fetch(CHAT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: finalPrompt,
          user: userParam,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}`);
      }

      const data = await response.json();
      console.log("Réponse Pepito complète:", data);

      const botMessage = {
        id: Date.now() + 1,
        sender: "pepito",
        text: data.message?.response || data.response || "Je n'ai pas pu générer de réponse.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.error("Pepito error:", err);
      const isTimeout = err.name === "AbortError";
      const errorMessage = {
        id: Date.now() + 1,
        sender: "pepito",
        text: isTimeout
          ? "Désolé, j'ai mis trop de temps à répondre. Réessaie ! 🍪"
          : "Désolé, je ne suis pas disponible pour le moment. Réessaie dans un instant ! 🍪",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto mt-10">
      {/* Header */}
      <div className="text-center mb-4">
        <h3 className="text-lg font-semibold font-[Manrope] text-foreground">
          Pépito 🍪 — Que puis-je faire pour toi aujourd'hui ?
        </h3>
      </div>

      {/* Chat container */}
      <div className="overflow-hidden">
        {/* Messages zone */}
        <div className="chat-scroll h-80 overflow-y-auto p-4 space-y-4 scroll-smooth">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
              <Cookie className="w-10 h-10 text-amber-500/40" />
              <p>Envoie un message pour commencer la conversation</p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex gap-3 ${
                msg.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.sender === "pepito" && (
                <div className="w-8 h-8 rounded-full bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <Cookie className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
              )}

              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                  msg.sender === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted text-foreground rounded-bl-md"
                }`}
              >
                {msg.sender === "pepito"
                  ? formatMessage(msg.text)
                  : msg.text}
              </div>

              {msg.sender === "user" && (
                <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-4 h-4 text-primary" />
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Pépito réfléchit...
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div className="p-3">
          <form onSubmit={handleSend} className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Écris un message à Pépito..."
              className="flex-1"
              disabled={isLoading}
              autoComplete="off"
            />
            <Button
              type="submit"
              size="icon"
              disabled={isLoading || !input.trim()}
              className="flex-shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
